import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

function InvoicesManager({ squareCustomerId }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    if (!squareCustomerId) return;
    fetchInvoices();
  }, [squareCustomerId]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const fetchInvoicesFn = httpsCallable(functions, "fetchCustomerInvoices");
      const response = await fetchInvoicesFn({ customerId: squareCustomerId });
      const { invoices: fetchedInvoices } = response.data || {};
      setInvoices(fetchedInvoices || []);
    } catch (error) {
      console.error("Error fetching invoices:", error.message);
      showSnackbar("Failed to fetch invoices. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (amount, currency) => {
    if (!amount || !currency) return "N/A";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(amount / 100);
  };

  const handleOpenDetails = (invoice) => {
    setSelectedInvoice(invoice);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setSelectedInvoice(null);
    setDetailsOpen(false);
  };

  const handleViewInvoice = () => {
    if (!selectedInvoice?.public_url) {
      showSnackbar("No public URL for this invoice.", "warning");
      return;
    }
    window.open(selectedInvoice.public_url, "_blank");
  };

  const handleVoidInvoice = async () => {
    if (!selectedInvoice) return;

    const voidInvoiceFn = httpsCallable(functions, "voidSquareInvoice");

    try {
      await voidInvoiceFn({
        invoiceId: selectedInvoice.id,
        version: selectedInvoice.version,
      });
      showSnackbar(`Invoice ${selectedInvoice.invoice_number} has been voided.`, "success");
      fetchInvoices();
      handleCloseDetails();
    } catch (error) {
      console.error("Error voiding invoice:", error.message);
      showSnackbar("Failed to void/delete invoice.", "error");
    }
  };

  const canVoidInvoice = (invoice) => {
    if (!invoice) return false;
    const { status } = invoice;

    return !["PAID", "CANCELED"].includes(status);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (invoices.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary">
          No invoices found for this customer.
        </Typography>
      );
    }

    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Invoice #</TableCell>
            <TableCell>Amount</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Subscription</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {invoices.map((inv) => {
            const paymentReq = inv.payment_requests?.[0] || {};
            const amount = paymentReq.computed_amount_money?.amount;
            const currency = paymentReq.computed_amount_money?.currency;

            return (
              <TableRow key={inv.id}>
                <TableCell>{inv.invoice_number || "—"}</TableCell>
                <TableCell>{formatMoney(amount, currency)}</TableCell>
                <TableCell>{inv.status || "N/A"}</TableCell>
                <TableCell>{inv.subscription_id || "—"}</TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleOpenDetails(inv)}
                  >
                    Details
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  const renderDetailsDialog = () => {
    if (!selectedInvoice) return null;

    const paymentReq = selectedInvoice.payment_requests?.[0] || {};
    const amount = paymentReq.computed_amount_money?.amount;
    const currency = paymentReq.computed_amount_money?.currency;
    const autoPaySource = paymentReq.automatic_payment_source || "NONE";
    const isAutoPay = autoPaySource !== "NONE";
    const storePaymentMethodEnabled = selectedInvoice.store_payment_method_enabled;
    const paidAmount = paymentReq.total_completed_amount_money?.amount || null;

    return (
      <Dialog open={detailsOpen} onClose={handleCloseDetails} maxWidth="sm" fullWidth>
        <DialogTitle>Invoice Details</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" gutterBottom>
            Invoice Number: {selectedInvoice.invoice_number}
          </Typography>
          <Typography variant="body2" gutterBottom>
            Title: {selectedInvoice.title || "—"}
          </Typography>
          <Typography variant="body2" gutterBottom>
            Status: {selectedInvoice.status}
          </Typography>
          <Typography variant="body2" gutterBottom>
            Subscription ID: {selectedInvoice.subscription_id || "—"}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" gutterBottom>
            Amount Due: {formatMoney(amount, currency)}
          </Typography>
          <Typography variant="body2" gutterBottom>
            Paid Amount: {paidAmount ? formatMoney(paidAmount, currency) : "—"}
          </Typography>
          <Typography variant="body2" gutterBottom>
            Due Date: {paymentReq.due_date || "—"}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" gutterBottom>
            Auto Payment Source: {autoPaySource}
          </Typography>
          <Typography variant="body2" gutterBottom>
            Auto-Pay Enabled: {isAutoPay ? "Yes" : "No"}
          </Typography>
          <Typography variant="body2" gutterBottom>
            Store Payment Method Enabled: {storePaymentMethodEnabled ? "Yes" : "No"}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" gutterBottom>
            Last Updated:{" "}
            {selectedInvoice.updated_at
              ? new Date(selectedInvoice.updated_at).toLocaleString()
              : "—"}
          </Typography>
          <Typography variant="body2" gutterBottom>
            Created:{" "}
            {selectedInvoice.created_at
              ? new Date(selectedInvoice.created_at).toLocaleString()
              : "—"}
          </Typography>
        </DialogContent>
        <DialogActions>
          {canVoidInvoice(selectedInvoice) && (
            <Button variant="outlined" color="error" onClick={handleVoidInvoice}>
              Cancel Invoice
            </Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            color="primary"
            onClick={handleViewInvoice}
            disabled={!selectedInvoice.public_url}
          >
            View / Pay
          </Button>
          <Button variant="contained" onClick={handleCloseDetails}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      {renderContent()}
      {renderDetailsDialog()}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

InvoicesManager.propTypes = {
  squareCustomerId: PropTypes.string.isRequired,
};

export default InvoicesManager;
